import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/select';
import { Flag, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DEAL_STAGES = [
  { key: 'needs_analysis', pct: 30, label: 'Needs Analysis', cls: 'bg-slate-100 text-slate-700 border-slate-200', bar: 'bg-slate-400' },
  { key: 'solution_alignment', pct: 50, label: 'Solution Alignment', cls: 'bg-sky-50 text-sky-700 border-sky-200', bar: 'bg-sky-500' },
  { key: 'negotiation', pct: 70, label: 'Negotiation', cls: 'bg-amber-50 text-amber-800 border-amber-200', bar: 'bg-amber-500' },
  { key: 'contract_review', pct: 90, label: 'Contract Review', cls: 'bg-violet-50 text-violet-700 border-violet-200', bar: 'bg-violet-500' },
  { key: 'closed_won', pct: 100, label: 'Closed Won', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-600' },
];
export const STAGE_BY_KEY = Object.fromEntries(DEAL_STAGES.map((s) => [s.key, s]));
export const stageLabel = (key) => (STAGE_BY_KEY[key] ? `${STAGE_BY_KEY[key].pct}% · ${STAGE_BY_KEY[key].label}` : 'No stage');
export const stageClass = (key) => STAGE_BY_KEY[key]?.cls || 'bg-white text-slate-400 border-dashed border-slate-300';

/** Deal-stage chip (read-only) or dropdown (editable). Persists via PATCH /estimates/{id}/deal-stage; 100% Closed Won also marks the estimate Won. */
export default function DealStageSelect({ estimateId, stage, lost = false, canEdit = true, onChange, compact = false, testIdPrefix = 'deal-stage', effectiveAt = null }) {
  const [busy, setBusy] = useState(false);
  const change = async (v) => {
    const key = v === '__none__' ? null : v;
    if (key === 'closed_won' && !window.confirm('Setting 100% Closed Won also marks this estimate as Won (and sets the close date if empty). Continue?')) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API_URL}/api/estimates/${estimateId}/deal-stage`, { deal_stage: key, ...(effectiveAt ? { effective_at: effectiveAt } : {}) }, { withCredentials: true });
      toast.success(key ? `Deal stage set to ${stageLabel(key)}${key === 'closed_won' ? ' — estimate marked Won' : ''}` : 'Deal stage cleared');
      onChange?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the deal stage'); } finally { setBusy(false); }
  };
  const chip = (
    <span className={`!inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${stageClass(stage)}`} data-testid={`${testIdPrefix}-chip`}>
      <Flag className="w-3 h-3" />{stageLabel(stage)}
      {lost && <span className="ml-1 rounded-full bg-rose-100 text-rose-700 px-1.5 text-[9px]" data-testid={`${testIdPrefix}-lost`}>Lost</span>}
    </span>
  );
  if (!canEdit || lost || !estimateId) return chip;
  return (
    <Select value={stage || '__none__'} onValueChange={change} disabled={busy}>
      <SelectTrigger className={`h-auto w-auto shrink-0 border-0 bg-transparent p-0 shadow-none focus:ring-0 ${compact ? '' : ''} [&>svg]:hidden`} title="Deal stage — click to change" data-testid={`${testIdPrefix}-trigger`} onClick={(e) => e.stopPropagation()}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : chip}
      </SelectTrigger>
      <SelectContent onClick={(e) => e.stopPropagation()}>
        <SelectItem value="__none__" data-testid={`${testIdPrefix}-opt-none`}>No stage</SelectItem>
        {DEAL_STAGES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`${testIdPrefix}-opt-${s.key}`}>{s.pct}% · {s.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
